import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../../../environments/environment';

/** Prefix for AES-CBC payloads written by this service (localStorage only). */
const LS_CIPHER_PREFIX = 'enc2.';
const IV_LEN_BYTES = 16;

/**
 * Sync AES-256-CBC seal/unseal for localStorage payloads.
 * Obfuscation only: key material is in the client bundle; XSS can still read plaintext in memory.
 * Does not touch `localStorage` — {@link StorageService} is the wrapper.
 */
@Injectable()
export class LocalStorageCipherService {
  /** Encrypt on write. Independent of decrypt: keep keyPhrase to open existing enc2. values. */
  isEnabled(): boolean {
    const c = environment.localStorageCrypto;
    return !!c?.enabled && this.canDecrypt();
  }

  /** True when keyPhrase is present so existing enc2. payloads can still be opened. */
  canDecrypt(): boolean {
    const phrase = environment.localStorageCrypto?.keyPhrase;
    return typeof phrase === 'string' && phrase.length > 0;
  }

  isEnc2Payload(stored: string | null): boolean {
    return !!stored?.startsWith(LS_CIPHER_PREFIX);
  }

  seal(plainUtf8: string): string {
    if (!this.isEnabled()) {
      return plainUtf8;
    }
    const phrase = environment.localStorageCrypto!.keyPhrase;
    const keyWa = CryptoJS.SHA256(CryptoJS.enc.Utf8.parse(phrase));
    const iv = CryptoJS.lib.WordArray.random(IV_LEN_BYTES);
    const encrypted = CryptoJS.AES.encrypt(plainUtf8, keyWa, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const combined = iv.concat(encrypted.ciphertext);
    return LS_CIPHER_PREFIX + CryptoJS.enc.Base64.stringify(combined);
  }

  /**
   * @returns inner UTF-8 string if `stored` is a valid enc2 payload; otherwise `null` (caller may treat as legacy plaintext).
   * Decrypts whenever keyPhrase is set, even if encrypt-on-write is off.
   */
  open(stored: string): string | null {
    if (!this.isEnc2Payload(stored)) {
      return null;
    }
    if (!this.canDecrypt()) {
      return null;
    }
    try {
      const phrase = environment.localStorageCrypto!.keyPhrase;
      const keyWa = CryptoJS.SHA256(CryptoJS.enc.Utf8.parse(phrase));
      const combined = CryptoJS.enc.Base64.parse(stored.slice(LS_CIPHER_PREFIX.length));
      if (combined.sigBytes < IV_LEN_BYTES) {
        return null;
      }
      const iv = CryptoJS.lib.WordArray.create(
        combined.words.slice(0, IV_LEN_BYTES / 4),
        IV_LEN_BYTES,
      );
      const ct = CryptoJS.lib.WordArray.create(
        combined.words.slice(IV_LEN_BYTES / 4),
        combined.sigBytes - IV_LEN_BYTES,
      );
      const params = CryptoJS.lib.CipherParams.create({ ciphertext: ct });
      const dec = CryptoJS.AES.decrypt(params, keyWa, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      return dec.toString(CryptoJS.enc.Utf8);
    } catch {
      return null;
    }
  }
}
